import React, {useCallback, useMemo, useState} from 'react'
import {TitledBody} from '../common/components/TitledBody'
import {useTranslation} from 'react-i18next'
import {Alert, Container, FormLabel, Grid, IconButton, Menu, Paper, Snackbar} from '@mui/material'
import {LicenceStats} from './LicenceStats'
import {CpeStats} from './CpeStats'
import {addUrl as addIvrUrl} from '../ivrs'
import {useHistory} from 'react-router'
import {useFlowStats} from './FlowStats'
import {useIvrStats} from './IvrStats'
import {MainNumber} from './MainNumber'
import {UserOptions} from './UserOptions'
import {addUrlHuntGroup, addUrlPremiumCallCenter, addUrlQueue} from '../flows'
import {Alias} from './Alias'
import {useSelector} from 'react-redux'
import {
    businessReportingEnabledSelector,
    multimediaContactCenterEnabledSelector,
    premiumCallCenterAllowedForLicenseSelector,
    premiumCallCenterEnabledSelector,
    selectedSiteSelector
} from '../authentication/redux'
import SettingsIcon from '@mui/icons-material/Settings'
import MenuItem from '@mui/material/MenuItem'
import {usePrivilege} from '../common/util/hooks'
import {FeaturePrivileges} from '../common/util/featurePrivileges'
import {api} from '../common/api'

export const Group = () => {
    const { t } = useTranslation()
    const history = useHistory()
    const site = useSelector(selectedSiteSelector)
    const { hasAccess: hasWriteAccess } = usePrivilege(FeaturePrivileges.site.write)
    const businessReportingEnabled = useSelector(businessReportingEnabledSelector)
    const multimediaContactCenterEnabled = useSelector(multimediaContactCenterEnabledSelector)
    const [akixiSyncSuccess, setAkixiSyncSuccess] = useState<boolean>(false)
    const [akixiSyncError, setAkixiSyncError] = useState<boolean>(false)
    const premiumCallCenterEnabled = useSelector(premiumCallCenterEnabledSelector)
    const premiumCallCenterAllowedForLicense = useSelector(premiumCallCenterAllowedForLicenseSelector)
    const [time, setTime] = useState(0);

    //disable Akixi-button for a while to discourage fast clicking
    const akixiButtonPressed = useCallback(() => {
        setTime(20);
        let timer = setInterval(() => {
            setTime((time) => {
                if (time === 0) {
                    clearInterval(timer);
                    return 0;
                } else return time - 1;
            });
        }, 1000);
    }, []);

    const { stats: ivrStats } = useIvrStats({
        onClick: () => {
            history.push(addIvrUrl)
            return null
        },
    })

    const { stats: huntGroupStats } = useFlowStats({
        onClick: () => {
            history.push(addUrlHuntGroup)
        },
        type: "HUNT_GROUP"
    })

    const { stats: queueStats } = useFlowStats({
        onClick: () => {
            history.push(addUrlQueue)
        },
        type: "QUEUE",
        onlyPremium: false
    })

    const { stats: premiumQueueStats } = useFlowStats({
        onClick: () => {
            history.push(addUrlPremiumCallCenter)
        },
        type: "QUEUE",
        onlyPremium: true
    })

    const [anchorEl, setAnchorEl] = React.useState(null);

    const handleClick = (event: any) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = useCallback(() => {
        setAnchorEl(null);
    }, [setAnchorEl]);

    const syncAkixi = useCallback(async (): Promise<void> => {
        if (!site) return
        try {
            akixiButtonPressed();
            await api.put(`/api/site/${site.productId}/sync-akixi`)
            setAkixiSyncSuccess(true)
            setAkixiSyncError(false)
        } catch (e) {
            setAkixiSyncSuccess(false)
            setAkixiSyncError(true)
        }
    }, [site, setAkixiSyncError, setAkixiSyncSuccess, akixiButtonPressed])

    const buttonsPanel = useMemo(() =>
        hasWriteAccess && (businessReportingEnabled || multimediaContactCenterEnabled) ?
            <Grid container item justifyContent={'space-between'} flexDirection={'row'} spacing={2} flexWrap={'nowrap'}>
                <Grid item xs={'auto'} pr={1}>
                    <IconButton color="secondary" onClick={handleClick}>
                        <SettingsIcon/>
                    </IconButton>
                    <Menu
                        id="simple-menu"
                        anchorEl={anchorEl}
                        keepMounted
                        open={Boolean(anchorEl)}
                        onClose={handleClose}
                    >
                        <MenuItem disabled={time > 0} onClick={async () => {
                            await syncAkixi(); handleClose();
                        }}>{t("site.sync-akixi")}</MenuItem>
                    </Menu>
                </Grid>
            </Grid> : <></>, [t, time, handleClose, syncAkixi, anchorEl, hasWriteAccess, businessReportingEnabled, multimediaContactCenterEnabled])

    return (
        <TitledBody title={t('site.title')} buttonsPanel={buttonsPanel}>
            <Container sx={{ mt: 2, mb: 2 }}>
                <Grid container spacing={4}  sx={{ mb: 4 }}>
                    <Grid item xs={12} >
                        <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column' }}>
                            <Grid container direction='row'
                                  justifyContent='start'
                                  alignItems='center' spacing={0}>
                                <Grid item xs={12} md={3} lg={2} xl={2} sx={{ paddingRight: 1 }}>
                                    <FormLabel>{`Alias:`}</FormLabel>
                                </Grid>
                                < Grid item xs>
                                    <Alias productId={site?.productId}/>
                                </Grid>
                            </Grid>
                        </Paper>
                        <Paper sx={{ mt: 2, p: 2, display: 'flex', flexDirection: 'column' }}>
                            <MainNumber />
                        </Paper>
                    </Grid>
                </Grid>
                <Grid container spacing={4}>
                    <Grid item xs={12} lg={4} md={6}>
                        <LicenceStats />
                    </Grid>
                    <Grid item xs={12} lg={4} md={6}>
                        <UserOptions />
                    </Grid>
                    <Grid item xs={12} lg={4} md={6}>
                        <CpeStats />
                    </Grid>
                    <Grid item xs={12} lg={4} md={6}>
                        {huntGroupStats}
                    </Grid>
                    <Grid item xs={12} lg={4} md={6}>
                        {queueStats}
                    </Grid>
                    <Grid item xs={12} lg={4} md={6}>
                        {ivrStats}
                    </Grid>
                    {premiumCallCenterEnabled && premiumCallCenterAllowedForLicense &&
                        <Grid item xs={12} lg={4} md={6}>
                            {premiumQueueStats}
                        </Grid>
                    }
                </Grid>
            </Container>
            <Snackbar anchorOrigin={{ vertical: 'top', horizontal: 'right' }} open={akixiSyncError || akixiSyncSuccess} autoHideDuration={3000} onClose={() => {setAkixiSyncError(false); setAkixiSyncSuccess(false);}}>
                <Alert onClose={() => {setAkixiSyncError(false); setAkixiSyncSuccess(false)}} severity={akixiSyncError? 'error' : 'success'} sx={{ width: '100%' }}>
                    {akixiSyncError ? t('general.unknown-server-error') : t('site.sync-akixi-success')}
                </Alert>
            </Snackbar>
        </TitledBody>
    )
}
